import { IProvider } from '@web3auth/modal';
import { TransactionReceipt, Web3 } from 'web3';
// import { type RegisteredSubscription } from 'web3/lib/commonjs/eth.exports';

import { ContractBalanceOf, SendErc721Type } from '@/types';
import Erc721Abi from './abi/Erc721Abi.json';
import Erc1155Abi from './abi/Erc1155Abi.json';
import { ERC721, POLYGON_AMOY, POLYGON_MAIN } from '../common/name-space';
import networkList from '@/component/wallet/NetworkList';
import { fiexdDecimal, NumWithComma } from '../BigNumbers';
import { weiToEth } from './utils';
import { ResPrivateListType } from '@/types/staking/read';
import Erc20PolygonSflTrlabi from '@/lib/web3/abi/Erc20PolygonSflTrlabi.json';
// import erc20abi from './erc20abi.json';

export default class EthereumRpc {
  private provider: IProvider;
  private web3: Web3;
  private contracts: Record<string, any>;
  private isInitialized: boolean = false;

  constructor(provider: IProvider) {
    this.provider = provider;
    this.web3 = new Web3(provider);
    this.contracts = {};

    // this.stakingContract();
  }
  // 정적 팩토리 메서드 - 비동기 초기화를 위한 권장 방법
  static async create(provider: IProvider): Promise<EthereumRpc> {
    const instance = new EthereumRpc(provider);
    await instance.loadContract();
    return instance;
  }

  // 기존 constructor 호출을 위한 호환성 메서드
  static async initialize(provider: IProvider): Promise<EthereumRpc> {
    return this.create(provider);
  }
  async loadContract() {
    try {
      const chainId = process.env.NODE_ENV === 'development' ? POLYGON_AMOY : POLYGON_MAIN;
      const networks = await networkList(chainId);
      networks.forEach((network) => {
        const { symbol, token, abi } = network;

        if (abi && token) {
          const contract = new this.web3.eth.Contract(abi, token);
          this.contracts[symbol] = contract;
        } else {
          console.warn(`ABI or contract address not found for symbol: ${symbol}`);
        }
      });
      this.isInitialized = true;
    } catch (error) {
      console.error('컨트랙트 로딩 중 오류 발생:', error);
      this.isInitialized = false;
    }
  }

  // 초기화 상태 확인
  isReady(): boolean {
    return this.isInitialized;
  }
  // 주로 프라이빗 토큰 등록으로 사용됨
  addContract(list: ResPrivateListType[]) {
    for (const iter of list) {
      const contract = new this.web3.eth.Contract(Erc20PolygonSflTrlabi, iter.extraContractAddr);
      this.contracts[iter.privateTokenSymbol] = contract;
    }
  }
  async contractCall<T>(aPayload: any): Promise<any> {
    const { method, symbol, address } = aPayload;
    const re: T = await this.contracts[symbol].methods[method](address).call();
    return re;
  }
  async contractSend(aPayload: any): Promise<string> {
    // async contractSend(aPayload: SendContractType): Promise<string> {
    const { method, symbol, from, data } = aPayload;
    const gasEstimate = await this.contracts[symbol].methods[method](...data).estimateGas({ from: from });
    const re: TransactionReceipt = await this.contracts[symbol].methods[method](...data).send({
      from: from,
      gas: gasEstimate.toString(),
    });
    return re.transactionHash.toString();
  }
  async sendNft(aPayload: SendErc721Type): Promise<string> {
    return aPayload.tokenType === ERC721 ? await this.send721(aPayload) : await this.send1155(aPayload);
  }
  private async send721(aPayload: SendErc721Type) {
    const { contractAddr, from, to, tokenId } = aPayload;
    const contract = new this.web3.eth.Contract(Erc721Abi, contractAddr);

    const gasEstimate = await contract.methods.safeTransferFrom(from, to, tokenId).estimateGas({ from: from });
    const re = await contract.methods
      .safeTransferFrom(from, to, tokenId)
      .send({ from: from, gas: gasEstimate.toString() });

    return re.transactionHash.toString();
  }
  private async send1155(aPayload: SendErc721Type) {
    const { contractAddr, from, to, tokenId } = aPayload;
    const contract = new this.web3.eth.Contract(Erc1155Abi, contractAddr);
    const amount = '1';

    const gasEstimate = await contract.methods
      .safeTransferFrom(from, to, tokenId, amount, '0x')
      .estimateGas({ from: from });

    const re = await contract.methods
      .safeTransferFrom(from, to, tokenId, amount, '0x')
      .send({ from: from, gas: gasEstimate.toString() });

    return re.transactionHash.toString();
  }
  async getAccounts(): Promise<string[]> {
    try {
      // const web3 = new Web3(this.provider);
      const accounts = await this.web3.eth.getAccounts();
      return accounts;
    } catch (error: unknown) {
      return error as string[];
    }
  }
  async getAddress0(): Promise<string> {
    const addr = await this.getAccounts();
    const address = addr[0];
    return address;
  }
  async getBalance(address: string): Promise<string> {
    try {
      const balance = await this.web3.eth.getBalance(address);
      return this.web3.utils.fromWei(balance.toString(), 'ether') || '0';
    } catch (error) {
      console.log(error);
      return '0';
    }
  }
  async getTokenBalance(aPayload: ContractBalanceOf): Promise<string> {
    const balance = await this.contractCall<ContractBalanceOf>(aPayload);
    return NumWithComma(fiexdDecimal(weiToEth(balance)));
  }
  async signAndSendTransaction(aToAddr: string, aAmount: string): Promise<string> {
    try {
      const web3 = new Web3(this.provider);
      const accounts = await web3.eth.getAccounts();

      const txRes = await web3.eth.sendTransaction({
        from: accounts[0],
        to: aToAddr,
        value: aAmount, // web3.utils.toWei('0.01', 'ether'),
      });
      return txRes.transactionHash.toString();
    } catch (error) {
      return error as string;
    }
  }
}
